import React, { useState, useContext, useEffect } from 'react';
import { useNavigate, useLocation, Link } from 'react-router-dom';
import { Eye, EyeOff } from 'lucide-react';
import { AuthContext } from '../../context/AuthContext';
import { ToastContext } from '../../context/ToastContext';
import MainLayout from '../../components/layout/MainLayout';
import Button from '../../components/common/Button';
import Input from '../../components/common/Input';

const REMEMBER_KEY = 'rememberedEmail';

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useContext(AuthContext);
  const { showToast } = useContext(ToastContext);

  // 로그인 후 복귀 경로 (ThankYouPage 등에서 state.from 으로 전달)
  const from = location.state?.from || '/';

  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [formError, setFormError] = useState('');

  // 저장된 아이디(이메일) 복원
  useEffect(() => {
    const saved = localStorage.getItem(REMEMBER_KEY);
    if (saved) {
      setFormData((prev) => ({ ...prev, email: saved }));
      setRememberMe(true);
    }
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
    if (formError) setFormError('');
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.email) {
      newErrors.email = '이메일을 입력해주세요.';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = '올바른 이메일 형식이 아닙니다.';
    }

    if (!formData.password) {
      newErrors.password = '비밀번호를 입력해주세요.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError('');

    if (!validateForm()) return;

    setLoading(true);

    try {
      const result = await login(formData.email, formData.password);

      if (result.success) {
        // 아이디 저장 처리
        if (rememberMe) {
          localStorage.setItem(REMEMBER_KEY, formData.email);
        } else {
          localStorage.removeItem(REMEMBER_KEY);
        }
        showToast('로그인되었습니다.', 'success');
        navigate(from, { replace: true });
      } else {
        const msg = result.error || '로그인에 실패했습니다.';
        setFormError(msg);
        showToast(msg, 'error');
      }
    } catch (error) {
      const msg = '로그인 중 오류가 발생했습니다.';
      setFormError(msg);
      showToast(msg, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialLogin = (provider) => {
    showToast(`${provider} 로그인은 준비 중입니다.`, 'info');
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    showToast('비밀번호 찾기 기능은 준비 중입니다.', 'info');
  };

  return (
    <MainLayout>
      <div className="bg-secondary-100 flex items-center justify-center px-4 py-12 min-h-[calc(100vh-200px)]">
        <div className="max-w-md w-full">
        {/* 로고 및 타이틀 */}
        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-primary mb-2">
            로그인
          </h1>
          <p className="text-accent-600">
            장례 서비스를 이용하시려면 로그인해주세요.
          </p>
        </div>

        {/* 로그인 폼 */}
        <div className="bg-white rounded-lg shadow-md p-8">
          {/* 폼 상단 인라인 에러 영역 */}
          <div aria-live="assertive" role="alert">
            {formError && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {formError}
              </div>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <Input
              type="email"
              name="email"
              label="이메일"
              value={formData.email}
              onChange={handleChange}
              placeholder="example@email.com"
              error={errors.email}
              required
            />

            {/* 비밀번호 + 표시 토글 */}
            <div className="w-full">
              <label htmlFor="password" className="block text-sm font-medium text-primary mb-1">
                비밀번호<span className="text-red-600 ml-1">*</span>
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  name="password"
                  id="password"
                  value={formData.password}
                  onChange={handleChange}
                  placeholder="비밀번호를 입력하세요"
                  required
                  className={`
                    w-full px-4 py-2 pr-11 rounded-lg border
                    ${errors.password ? 'border-red-500 focus:ring-red-500' : 'border-accent-300 focus:ring-primary-500'}
                    bg-white text-primary placeholder-accent-400
                    focus:outline-none focus:ring-2 focus:border-transparent
                    transition-all duration-200
                  `}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword((v) => !v)}
                  aria-label={showPassword ? '비밀번호 숨기기' : '비밀번호 표시'}
                  aria-pressed={showPassword}
                  className="absolute inset-y-0 right-0 flex items-center px-3 text-accent-500 hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-r-lg"
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
              {errors.password && (
                <p className="mt-1 text-sm text-red-600">{errors.password}</p>
              )}
            </div>

            {/* 아이디 저장 + 비밀번호 찾기 */}
            <div className="flex items-center justify-between">
              <label className="flex items-center gap-2 text-sm text-accent-700 cursor-pointer">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-accent-300 text-primary focus:ring-primary-500"
                />
                아이디 저장
              </label>
              <a
                href="/login"
                onClick={handleForgotPassword}
                className="text-sm text-primary font-medium hover:underline"
              >
                비밀번호 찾기
              </a>
            </div>

            <Button
              type="submit"
              variant="primary"
              fullWidth
              disabled={loading}
            >
              {loading ? '로그인 중...' : '로그인'}
            </Button>
          </form>

          {/* 소셜 로그인 구분선 */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-accent-300"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white text-accent-600">또는</span>
            </div>
          </div>

          {/* 소셜 로그인 버튼 */}
          <div className="space-y-3">
            <button
              type="button"
              onClick={() => handleSocialLogin('카카오')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#FEE500] text-[#191600] font-medium hover:bg-[#FADA0A] transition-colors duration-200"
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 items-center justify-center rounded-full bg-[#191600] text-[10px] font-bold text-[#FEE500]"
              >
                K
              </span>
              <span>카카오 로그인</span>
            </button>

            <button
              type="button"
              onClick={() => handleSocialLogin('네이버')}
              className="w-full flex items-center justify-center gap-2 px-4 py-2 rounded-lg bg-[#03C75A] text-white font-medium hover:bg-[#02B350] transition-colors duration-200"
            >
              <span
                aria-hidden="true"
                className="flex h-5 w-5 items-center justify-center rounded bg-white text-[12px] font-extrabold text-[#03C75A]"
              >
                N
              </span>
              <span>네이버 로그인</span>
            </button>
          </div>

          {/* 회원가입 링크 */}
          <div className="mt-6 text-center">
            <p className="text-sm text-accent-600">
              계정이 없으신가요?{' '}
              <Link
                to="/signup"
                className="text-primary font-medium hover:text-primary-700 transition-colors"
              >
                회원가입
              </Link>
            </p>
          </div>
        </div>

        {/* 테스트 계정 안내 */}
        <div className="mt-4 p-4 bg-accent-50 rounded-lg border border-accent-200">
          <p className="text-xs text-accent-700 text-center">
            <strong>테스트 계정:</strong> user1@example.com / password123
          </p>
        </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default LoginPage;
